import type { IkeRequestError } from '@ikegps/core-shared';
import { action, observable, makeObservable } from 'mobx';
import { T, type TMessage, Translate } from '../../i18n/index';
import { m, type MithrilVNode } from '../../mithril';
import { Logger, Services } from '../../services';
import type { InputState } from '../input/input.state';
import { IkeInputStateGroup } from '../input/input.state.group';
import { IMithrilStateComponent } from '../mithril.component';
import { ModalService } from '../modal/modal.service';
import { NotificationService } from '../notification.toast/notification.service';
import { Spinner } from '../spinner/spinner';

export interface ModalFormComponentCtx {
	title: T;
	confirm: T | (() => T);
	success: TMessage;
	cancel?: T | (() => T);
	/** Show a close (X) button in the modal's title bar */
	hasClose?: boolean;
}

export abstract class ModalFormComponent extends IMithrilStateComponent {
	@observable loading: boolean = false;

	form = new IkeInputStateGroup();
	@observable displaySubmitButton: boolean = true;
	@observable isSubmitButtonDisabled: boolean = false;
	@observable displayCancelButton: boolean = true;
	@observable displayCustomButtons: boolean = false;

	ctx: ModalFormComponentCtx;

	constructor(ctx: ModalFormComponentCtx) {
		super();
		makeObservable(this);

		this.ctx = ctx;
	}

	override onCreate() {
		if (this.validate != null) {
			this.autorun(() => this.validate?.());
		}
	}

	@action open() {
		if (ModalService.isOpen) {
			return;
		}

		this.reset();

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		ModalService.open({
			title: this.ctx.title,
			content: () => this.view(),
			actions: () => this.viewActions(),
			onSubmit: () => this.wrapSubmit(),
			canClose: () => this.canClose(),
			onClose: this.ctx.hasClose ? () => this.close() : undefined,
		}).then(() => this.reset());
	}

	@action close() {
		this.reset();
		ModalService.close(false);
	}

	@action reset() {
		this.form.reset();
		this.loading = false;
	}

	validate?(): void;
	submit?(): Promise<unknown>;
	failToChange?(e?: IkeRequestError): void;

	protected addState<InputStateValueType = string>(input: InputState<InputStateValueType>) {
		this.form.add(input);
	}

	@action protected setLoading(loading: boolean) {
		this.loading = loading;
	}

	protected canClose() {
		return !this.loading;
	}

	protected viewSubmitButton() {
		const confirmContent = typeof this.ctx.confirm === 'function' ? this.ctx.confirm() : this.ctx.confirm;
		const submitChildren = this.loading ? Spinner.view() : Translate(confirmContent);

		return m(
			'button',
			{
				className: 'mdl-button mdl-button--accent',
				disabled: !this.form.canSubmit || this.loading || this.isSubmitButtonDisabled,
				type: 'submit',
			},
			submitChildren
		);
	}

	protected viewCancelButton() {
		const cancelContent = typeof this.ctx.cancel === 'function' ? this.ctx.cancel() : this.ctx.cancel;
		const cancelText = cancelContent ? Translate(cancelContent) : Translate(T.CANCEL);

		return m(
			'button',
			{ className: 'mdl-button', onclick: () => this.close(), disabled: this.loading, type: 'button' },
			cancelText
		);
	}

	/**
	 * Set custom buttons to display instead of the default Cancel/Submit buttons.
	 * Custom buttons will not automatically close the modal, so you must call `this.close()` manually.
	 *
	 * Note: Needs to be enabled by setting `displayCustomButtons` to true.
	 */
	protected customButtons(): MithrilVNode[] {
		return [];
	}

	private viewActions() {
		const actionButtons: MithrilVNode[] = [];

		if (this.displayCustomButtons) {
			actionButtons.push(...this.customButtons());
		} else {
			if (this.displayCancelButton) {
				actionButtons.push(this.viewCancelButton());
			}
			if (this.displaySubmitButton) {
				actionButtons.push(this.viewSubmitButton());
			}
		}

		return m.fragment({}, actionButtons);
	}

	private wrapSubmit() {
		if (!this.form.canSubmit) {
			Logger.error('Attempted to submit invalid form...');
			return false;
		}

		this.setLoading(true);

		return Promise.resolve()
			.then(() => (this.submit != null ? this.submit() : null))
			.then(() => !Services.License.hasNoLicense && NotificationService.success(this.ctx.success))
			.then(() => this.close())
			.catch((e) => {
				Logger.error(e);
				NotificationService.error({ key: T.ERROR_GENERAL });
				this.setLoading(false);

				if (this.failToChange != null) {
					this.failToChange(e);
				}
			});
	}
}
